import React, { useEffect, useState, useCallback } from "react";
import { LuUser } from "react-icons/lu";
import { TbCalendarTime } from "react-icons/tb";
import { getStatusBgColor, statusIcons } from "../../Enum/LeadEnumConstants";
import FollowUpsService from "../Services/FollowUpsService";
import { formatDate, getIcons } from "../Constant/Constant";
import { followUpsStages } from "../../Enum/FollowUpsEnumConstants";
import DashedCircleLoader from "../pipelineComponents/DashedCircleLoader";
import { getBasePathByUserRole } from "../../utils/roleRoutes";
import { useNavigate } from "react-router-dom";
import { teamIds, userRole, userUid, teamList, teamDetails } from "../../utils/storage";

const LeadFollowUpsList = ({
  activeTab,
  startDate,
  endDate,
  selectedTeam,
  setSelectedTeam,
  selectedUser,
  setSelectedUser,
  refreshTrigger
}) => {
  const [teamList, setTeamList] = useState([]);
  const [overAllCount, setOverAllCount] = useState({});
  const [bodSelectedUser, setBodSelectedUser] = useState(null);
  const icons = getIcons();
  const navigate = useNavigate();

  const [stageData, setStageData] = useState({
    "FOLLOW UPS": {
      data: [],
      count: 0,
      next: "",
      loading: false,
      hasMore: true,
    },
    "FOLLOWED BACK": {
      data: [],
      count: 0,
      next: "",
      loading: false,
      hasMore: true,
    },
    "NO UPDATE": {
      data: [],
      count: 0,
      next: "",
      loading: false,
      hasMore: true,
    },
  });

  const isBoD = userRole === "BOD";
  const isMD = userRole === "MD";
  const currentSelectedId = isBoD ? bodSelectedUser : selectedTeam;
  const currentSelectedUser = isBoD ? bodSelectedUser : selectedUser;

  const getTeamList = async () => {
    try {
      let body = {};
  
      if (isMD) {
        body = {
          startDate: formatDate(startDate),
          endDate: formatDate(endDate),
          userId: userUid,
          role: userRole,
        };
      } else {
        body = {
          startDate: formatDate(startDate),
          endDate: formatDate(endDate),
          role: userRole,
        };
      }
  
      const teamIdList = Array.isArray(teamDetails)
        ? teamDetails.map((team) => team.id)
        : [];
  
      if (isBoD && teamIdList.length > 0) {
        body.teamId = teamIdList[0];
      }
  
      const response = isBoD
        ? await FollowUpsService.getFollowUpsUser(body)
        : await FollowUpsService.getFollowUpsTeams(body);
  
      const responseData = response.data;
  
      setTeamList(isBoD ? responseData.userCounts : responseData.teamCounts);
      setOverAllCount(responseData.overAllCount);
    } catch (error) {
      console.error(`Error fetching ${isBoD ? "users" : "teams"}:`, error);
    }
  };
  
  

  useEffect(() => {
    getTeamList();
  }, [startDate, endDate, userUid]);

  const handleLeadInfo = (lead) => {
    let path = getBasePathByUserRole();
    navigate(`${path}/lead-info/${lead.id}`, { state: { lead } });
  };

  const fetchStageData = async (
    stageValue,
    nextToken = "",
    isLoadMore = false
  ) => {
    try {
      setStageData((prev) => ({
        ...prev,
        [stageValue]: { ...prev[stageValue], loading: true },
      }));

      const body = {
        limit: 10,
        next: nextToken,
        direction: isLoadMore ? "next" : "",
        startDate: formatDate(startDate),
        endDate: formatDate(endDate),
        followUpsType: stageValue,
        userId : currentSelectedUser?.value || "",
        role :  userRole
      };

      if (isBoD) {
        body.teamId = teamIds[0];
        body.userId = currentSelectedUser;
      } else if (isMD) {
         body.mdUid = userUid
      } else {
        body.teamId = currentSelectedId || "";
        body.userId = currentSelectedUser?.value || "";
      }

      const response = await FollowUpsService.getFollowUpsLeads(body);
      const { data, count, next } = response.data;

      const transformedData = data.map((item) => ({
        id: item.lead.id,
        name: item.lead.name,
        status: item.lead.status,
        taskName: item.taskName,
        userName: item.userName,
        date: item.followUpTimestamp,
      }));

      setStageData((prev) => ({
        ...prev,
        [stageValue]: {
          data: isLoadMore
            ? [...prev[stageValue].data, ...transformedData]
            : transformedData,
          count: count[stageValue] || 0,
          next: next || "",
          loading: false,
          hasMore: !!next,
        },
      }));
    } catch (error) {
      console.error(`Error fetching ${stageValue}:`, error);
      setStageData((prev) => ({
        ...prev,
        [stageValue]: { ...prev[stageValue], loading: false },
      }));
    }
  };

  const loadAllStages = async () => {
    const promises = followUpsStages.map((stage) =>
      fetchStageData(stage.value)
    );
    await Promise.all(promises);
  };

  const handleScroll = useCallback(
    (e, stageValue) => {
      const { scrollTop, scrollHeight, clientHeight } = e.target;

      if (scrollHeight - scrollTop <= clientHeight + 100) {
        const stage = stageData[stageValue];

        if (stage.hasMore && !stage.loading) {
          fetchStageData(stageValue, stage.next, true);
        }
      }
    },
    [stageData]
  );

  useEffect(() => {
    setStageData({
      "FOLLOW UPS": {
        data: [],
        count: 0,
        next: "",
        loading: false,
        hasMore: true,
      },
      "FOLLOWED BACK": {
        data: [],
        count: 0,
        next: "",
        loading: false,
        hasMore: true,
      },
      "NO UPDATE": {
        data: [],
        count: 0,
        next: "",
        loading: false,
        hasMore: true,
      },
    });

    loadAllStages();
  }, [selectedTeam, startDate, endDate, bodSelectedUser, selectedUser,refreshTrigger]);

  const handleTeamSelection = (teamId) => {
    if (isBoD) {
      setBodSelectedUser(bodSelectedUser === teamId ? null : teamId);
    } else {
      setSelectedTeam(selectedTeam === teamId ? null : teamId);
      setSelectedUser(null);
    }
  };

  const handleAllSelection = () => {
    if (isBoD) {
      setBodSelectedUser(null);
    } else {
      setSelectedTeam(null);
      setSelectedUser(null);
    }
  };

  const renderStageColumn = (
    stageKey,
    title,
    bgColor,
    borderColor,
    iconColor
  ) => {
    const stage = stageData[stageKey];

    return (
      <div className="flex flex-col gap-4">
        <div className="text-[24px] font-medium">
          {title} ({stage.count})
        </div>

        <div
          className="bg-white p-3 h-[calc(100vh-270px)] overflow-y-auto w-[362px] rounded-[10px] shadow"
          onScroll={(e) => handleScroll(e, stageKey)}
        >
          {stage.data.map((item) => {
            const matchedStatus = statusIcons.find(
              (statusItem) => statusItem.label === item.status
            );

            return (
              <div
                key={item.id}
                onClick={() => handleLeadInfo(item)}
                className={`flex mb-4 cursor-pointer items-start gap-4 ${bgColor} ${borderColor} rounded-[10px] px-4 py-4`}
              >
                <div className="flex flex-col w-full">
                  <div className="flex justify-between items-center mb-1">
                    <div title={item.name} className="text-base truncate w-[200px]  font-medium">{item.name}</div>
                    {matchedStatus && (
                      <div
                        className={`${getStatusBgColor(
                          matchedStatus.label
                        )} flex items-center px-2 py-1 gap-2 rounded-full`}
                      >
                        <img
                          src={matchedStatus.image}
                          alt={matchedStatus.label}
                          className="w-3 h-3"
                        />
                        <span className="text-gray-700 text-xs">
                          {matchedStatus.label}
                        </span>
                      </div>
                    )}
                  </div>

                  <div className="text-sm text-gray-600 mb-2">
                    Task Name: {item.taskName}
                  </div>

                  <div className="flex flex-col gap-2 text-sm text-gray-700">
                    <div className="flex items-center gap-2">
                      <LuUser className={`${iconColor} text-xl`} />
                      <div>{item.userName}</div>
                    </div>
                    <div className="flex items-center gap-2">
                      <TbCalendarTime className={`${iconColor} text-xl`} />
                      <div>{item.date}</div>
                    </div>
                  </div>
                </div>
              </div>
            );
          })}

          {stage.data.length === 0 && !stage.loading && (
            <div className="flex flex-col items-center justify-center h-full">
              <img
                src={icons.emptyFollowUp}
                alt="No Data"
                className="w-40 h-40"
              />
              <div className="text-gray-500 text-lg mt-4">No {title} Found</div>
            </div>
          )}

          {stage.loading && (
            <div className="flex justify-center py-4">
              <DashedCircleLoader />
            </div>
          )}
        </div>
      </div>
    );
  };

  const renderTeamSelectionColumn = () => {
    const selectionTitle = isBoD ? "Select User" : "Select Team";
    const allSelectionText = isBoD ? "All Users" : "All Teams";

    return (
      <div className="flex flex-col gap-4">
        <div className="text-[24px] font-medium">{selectionTitle}</div>
        <div className="bg-white relative h-[calc(100vh-270px)] overflow-y-auto w-[370px] rounded-[10px] shadow">
          {/* All Selection Option */}
          {overAllCount && (
            <div
              onClick={handleAllSelection}
              className={`p-3 rounded-lg mx-3 mt-3 top-0 sticky flex items-center justify-between mb-4 cursor-pointer transition-all duration-200 ${
                currentSelectedId === null
                  ? "bg-blue-50 border-2 border-blue-200 shadow-sm"
                  : "bg-gray-50 border border-gray-200 hover:bg-gray-100"
              }`}
            >
              <div className="text-base font-medium cursor-pointer truncate w-40">
                {allSelectionText}
              </div>
              <div className="flex items-center gap-2">
                <div className="flex items-center rounded-full bg-blue-50 border border-blue-200 h-8 w-8 text-center justify-center text-blue-600 text-sm font-medium">
                  {overAllCount.followUps || 0}
                </div>
                <div className="flex items-center rounded-full bg-green-50 border border-green-200 h-8 w-8 text-center justify-center text-green-600 text-sm font-medium">
                  {overAllCount.followedBack || 0}
                </div>
                <div className="flex items-center rounded-full bg-orange-50 border border-orange-200 h-8 w-8 text-center justify-center text-orange-600 text-sm font-medium">
                  {overAllCount.noUpdate || 0}
                </div>
              </div>
            </div>
          )}

          {/* Individual Team/User Items */}
          {teamList.map((item) => (
            <div
              key={item.id}
              onClick={() => handleTeamSelection(item.id)}
              className={`p-3 rounded-lg m-3 flex items-center justify-between mb-4 cursor-pointer transition-all duration-200 ${
                currentSelectedId === item.id
                  ? "bg-blue-50 border-2 border-blue-200 shadow-sm"
                  : "bg-gray-50 border border-gray-200 hover:bg-gray-100"
              }`}
            >
              <div className="text-base font-medium cursor-pointer truncate w-40">
                {item.name}
              </div>
              <div className="flex items-center gap-2">
                <div className="flex items-center rounded-full bg-blue-50 border border-blue-200 h-8 w-8 text-center justify-center text-blue-600 text-sm font-medium">
                  {item.counts?.followUps || 0}
                </div>
                <div className="flex items-center rounded-full bg-green-50 border border-green-200 h-8 w-8 text-center justify-center text-green-600 text-sm font-medium">
                  {item.counts?.followedBack || 0}
                </div>
                <div className="flex items-center rounded-full bg-orange-50 border border-orange-200 h-8 w-8 text-center justify-center text-orange-600 text-sm font-medium">
                  {item.counts?.noUpdate || 0}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  };

  return (
    <div className="h-screen overflow-x-auto pb-4 custom-scrollbar pipeline flex gap-6">
      {renderTeamSelectionColumn()}

      {/* Stage Columns */}
      {renderStageColumn(
        "FOLLOW UPS",
        "Follow Ups",
        "bg-blue-50",
        "border border-blue-200",
        "text-blue-600"
      )}

      {renderStageColumn(
        "FOLLOWED BACK",
        "Followed Back",
        "bg-green-50",
        "border border-green-200",
        "text-green-600"
      )}

      {renderStageColumn(
        "NO UPDATE",
        "No Update",
        "bg-orange-50",
        "border border-orange-200",
        "text-orange-600"
      )}
    </div>
  );
};

export default LeadFollowUpsList;
